import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import useAuth from 'hooks/useAuth'
import { PAGE_PRICE_LABEL_SYNC_HISTORY } from 'utils/constants'
import { fetchPriceLabelSyncHistoryUnseenCount } from 'utils/api/priceLabelSyncHistoryApi'
import { checkPageAccess } from 'utils/helpers'

type Props = {
  children: React.ReactNode
}

type PriceLabelUnseenCountContextType = {
  /** Unseen price label sync entries across all stores. 0 when unavailable. */
  unseenCount: number
  /**
   * Re-reads the count. Call it after marking entries as seen so the badge
   * drops immediately instead of waiting for a reload.
   */
  refreshUnseenCount: () => Promise<void>
}

export const PriceLabelUnseenCountContext = createContext<
  undefined | PriceLabelUnseenCountContextType
>(undefined)

/**
 * Holds the unseen price label sync count so the sidebar badge and the sync
 * history listing share one value. Kept out of the listing component because
 * the badge has to survive navigating away from that page.
 */
const PriceLabelUnseenCountProvider: React.FC<Props> = ({ children }) => {
  const { user, isAuthenticated } = useAuth()

  const [unseenCount, setUnseenCount] = useState<number>(0)

  // The endpoint 401s without view access to the page, so only ask for the
  // count when this user can actually open the sync history.
  const hasPageAccess = useMemo(
    () =>
      Boolean(
        checkPageAccess(PAGE_PRICE_LABEL_SYNC_HISTORY.id, user?.pageAccess)
          .page,
      ),
    [user?.pageAccess],
  )

  const refreshUnseenCount = useCallback(async () => {
    if (!isAuthenticated || !hasPageAccess) {
      setUnseenCount(0)
      return
    }

    try {
      const { data } = await fetchPriceLabelSyncHistoryUnseenCount()
      setUnseenCount(data?.data?.unseenCount || 0)
    } catch (error) {
      // The badge is decoration — a failed count should never toast or block.
      setUnseenCount(0)
    }
  }, [isAuthenticated, hasPageAccess])

  // Paints the badge on shell load. `hasPageAccess` is a boolean, so the
  // re-verify AuthProvider runs on every route change doesn't refetch this.
  useEffect(() => {
    refreshUnseenCount()
  }, [refreshUnseenCount])

  const value: PriceLabelUnseenCountContextType = useMemo(
    () => ({ unseenCount, refreshUnseenCount }),
    [unseenCount, refreshUnseenCount],
  )

  return (
    <PriceLabelUnseenCountContext.Provider value={value}>
      {children}
    </PriceLabelUnseenCountContext.Provider>
  )
}

export default PriceLabelUnseenCountProvider
